import { BRAND } from '@/config/brand';

/**
 * All human-facing time formatting is pinned to the club timezone
 * (Asia/Kolkata) regardless of the operator device's locale/timezone.
 */
const tz = BRAND.timezone;

const timeFmt = new Intl.DateTimeFormat(BRAND.locale, {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: tz,
});

const dateFmt = new Intl.DateTimeFormat(BRAND.locale, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: tz,
});

const weekdayFmt = new Intl.DateTimeFormat(BRAND.locale, {
  weekday: 'long',
  timeZone: tz,
});

export function formatClock(iso: string | Date): string {
  return timeFmt.format(new Date(iso));
}

export function formatDate(iso: string | Date): string {
  return dateFmt.format(new Date(iso));
}

export function formatWeekday(iso: string | Date): string {
  return weekdayFmt.format(new Date(iso));
}

export function nowISO(): string {
  return new Date().toISOString();
}

/** Milliseconds → "HH:MM:SS" for live station timers. */
export function formatDurationHMS(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Milliseconds → "1h 5m" style compact label. */
export function formatDurationCompact(ms: number): string {
  const totalMinutes = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Relative "5 min ago" / "2h ago" for activity feeds. */
export function timeAgo(iso: string | Date, from: Date = new Date()): string {
  const diffMs = from.getTime() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

/** Local minutes-since-midnight (in club tz) for a given instant. */
export function minutesOfDay(iso: string | Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: tz,
  }).formatToParts(new Date(iso));
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return hour * 60 + minute;
}

/** Day of week 0..6 (Sun..Sat) in club tz. */
export function dayOfWeek(iso: string | Date): number {
  const label = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone: tz,
  }).format(new Date(iso));
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(label);
}

/**
 * Minutes the club timezone is offset from UTC at a given instant (e.g. +330 for
 * Asia/Kolkata). Derived from Intl so it stays correct if the club tz ever
 * changes — no hardcoded offset.
 */
function clubOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // The wall-clock the club tz shows for `at`, interpreted as if it were UTC.
  const asUTC = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return Math.round((asUTC - at.getTime()) / 60_000);
}

/**
 * Compose an instant from a club-local 'YYYY-MM-DD' date + minute-of-day, pinned
 * to the club timezone (Asia/Kolkata) regardless of the device's timezone. So
 * "11:00 on 2026-09-21" always means 11:00 IST, and round-trips through
 * formatClock to the same wall-clock time on any device.
 */
export function clubDateTimeISO(dateISO: string, minuteOfDay: number): string {
  const [y, mo, d] = dateISO.split('-').map(Number);
  // First approximation: treat the wall time as UTC, then correct by the club
  // offset at that instant. Asia/Kolkata has no DST, so one pass is exact.
  const approx = Date.UTC(y ?? 1970, (mo ?? 1) - 1, d ?? 1) + minuteOfDay * 60_000;
  const offset = clubOffsetMinutes(new Date(approx));
  return new Date(approx - offset * 60_000).toISOString();
}

/** 'YYYY-MM-DD' for `at` (default now) in the club timezone. */
export function clubDateISO(at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** "18:30" ↔ minutes helpers for rate-window forms. */
export function minutesToHHMM(minute: number): string {
  const h = Math.floor(minute / 60) % 24;
  const m = minute % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

export function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
